import { CounterBadge } from '@fluentui/react-components';
import {
  AlertRegular,
  BotRegular,
  BuildingPeopleRegular,
  GaugeRegular,
  KeyMultipleRegular,
  MoneyRegular,
  ShieldCheckmarkRegular,
  ServerRegular,
} from '@fluentui/react-icons';
import { useMemo } from 'react';

import { useHubUser } from '@/auth/hooks';
import type { NavItem } from '@/design-system/NavRail';
import { useEnvStore } from '@/env/envStore';
import { canApproveSelectedEnvironment } from '@/features/elevation/approvalAccess';
import { usePendingElevationRequests } from '@/features/elevation/api';

export function useNavItems(): NavItem[] {
  const user = useHubUser();
  const env = useEnvStore((s) => s.env);
  const isCorp = env === 'corp';
  const canApprove = canApproveSelectedEnvironment(user.groups, env);
  const pending = usePendingElevationRequests({ enabled: canApprove });
  const pendingCount = useMemo(
    () => (canApprove ? pending.data?.length ?? 0 : 0),
    [canApprove, pending.data],
  );

  return useMemo<NavItem[]>(() => {
    if (isCorp) {
      return [{ to: '/ai-tools', label: 'AI tools', icon: <MoneyRegular />, end: true }];
    }

    const items: NavItem[] = [
      { to: '/', label: 'Dashboard', icon: <GaugeRegular />, end: true },
      { to: '/resources', label: 'Resources', icon: <ServerRegular /> },
      { to: '/business', label: 'Business', icon: <BuildingPeopleRegular /> },
      { to: '/incidents', label: 'Incidents', icon: <AlertRegular /> },
      { to: '/ai-usage', label: 'AI usage', icon: <BotRegular /> },
      { to: '/elevation', label: 'Elevation', icon: <KeyMultipleRegular />, end: true },
    ];
    if (canApprove) {
      items.push({
        to: '/elevation/approvals',
        label: 'Approvals',
        icon: <ShieldCheckmarkRegular />,
        badge:
          pendingCount > 0 ? (
            <CounterBadge count={pendingCount} appearance="filled" color="brand" />
          ) : undefined,
      });
    }
    return items;
  }, [isCorp, canApprove, pendingCount]);
}
